import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, RefreshCw, Users } from 'lucide-react'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { Forbidden } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import type { HierarchyNodeResponse } from '@/shared/api/types'
import { hierarchyLevel, mobilizerStatistics } from './api'
import { ROLES, RoleBadge, StatusChip } from './vocab'
import { MobilizerDrawer } from './MobilizerDrawer'

/**
 * The reporting line.
 *
 * ## One level per expand, always
 *
 * The backend can answer "everyone under X" in a single `<@` query — but that is
 * not a reason to render it. A national director's subtree is the entire roster,
 * and a tree that fetches and paints four thousand nodes to show the top six is
 * a screen that never finishes. Each expand is its own request for its own
 * level, and collapsing keeps what was already fetched so re-expanding is free.
 *
 * ## No drag-to-reparent
 *
 * Moving a supervisor rewrites the whole subtree's paths and takes a lock while
 * it does. That is a decision with a confirm dialog and a count of the people
 * affected — it lives in the drawer, deliberately, not on the end of a mouse
 * gesture where an accidental drop reorganises a county.
 */
export function MobilizerHierarchyPage() {
  const canRead = useCan('mobilizer:read')
  const [openId, setOpenId] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  const stats = useLoad(
    () => (canRead ? mobilizerStatistics({}) : Promise.resolve(null)), [canRead, version])

  const roots = useLoad(
    () => (canRead ? hierarchyLevel(undefined) : Promise.resolve([])), [canRead, version])

  if (!canRead) return <Forbidden what="the reporting line" />

  const s = stats.data
  const byRole = s?.byRole ?? {}
  const orderedRoles = Object.keys(byRole)
    .sort((a, b) => (ROLES[a as keyof typeof ROLES]?.level ?? 99)
      - (ROLES[b as keyof typeof ROLES]?.level ?? 99))
  const maxRole = Math.max(1, ...Object.values(byRole))

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Hierarchy & roster"
        description="Who reports to whom. Expanding fetches one level — the whole tree is never loaded, because a national director's subtree is the entire roster."
        right={(
          <button type="button" className="erp-btn" aria-label="Refresh"
                  onClick={() => setVersion((v) => v + 1)}>
            <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
          </button>
        )}
      />

      <KpiStrip>
        <MetricCard label="Mobilizers" value={fmtNumber(s?.total ?? 0)} sub="in the network" accent />
        <MetricCard label="Active" value={fmtNumber(s?.active ?? 0)}
                    sub={s ? `${s.total - s.active} not currently working` : ' '} />
        <MetricCard label="At the top" value={fmtNumber(roots.data?.length ?? 0)}
                    sub="people with no supervisor" />
        <MetricCard label="With a login" value={fmtNumber(s?.withUserAccount ?? 0)}
                    sub="can open the field screen" />
      </KpiStrip>

      <ErrorStrip message={roots.error ?? stats.error} />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Panel title="Reporting line"
               description="Click anybody to open their record. Changing a supervisor happens there, with a count of who moves.">
          {roots.loading && !roots.data ? (
            <LoadingBlock label="Loading the top of the tree…" />
          ) : (roots.data ?? []).length === 0 ? (
            <EmptyState
              title="Nobody at the top"
              hint="Every mobilizer here reports to somebody outside your scope, or the roster is empty."
            />
          ) : (
            <ul className="space-y-0.5">
              {(roots.data ?? []).map((n) => (
                <TreeNode key={n.id} node={n} depth={0} onOpen={setOpenId} refreshKey={version} />
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="By role" description="Ordered by seniority, not by size.">
          {orderedRoles.length === 0 ? (
            <EmptyState title="Nothing to count" hint="No mobilizers in your scope yet." />
          ) : (
            <ul className="space-y-2">
              {orderedRoles.map((r) => (
                <li key={r} className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <RoleBadge code={r} />
                    <span className="erp-metric text-erp-sm">{byRole[r]}</span>
                  </div>
                  {/* Normalised against the largest role, so the bars compare with
                      each other rather than with an arbitrary constant. */}
                  <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
                    <div className="h-full bg-chocolate"
                         style={{ width: `${((byRole[r] ?? 0) / maxRole) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Keyed on the subject — see the note in RosterPage. */}
      <MobilizerDrawer
        key={openId ?? 'closed'}
        id={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => setVersion((v) => v + 1)}
        onOpenOther={(id) => setOpenId(id)}
      />
    </div>
  )
}

function TreeNode({
  node, depth, onOpen, refreshKey,
}: {
  node: HierarchyNodeResponse
  depth: number
  onOpen: (id: string) => void
  refreshKey: number
}) {
  const [open, setOpen] = useState(false)
  const [children, setChildren] = useState<HierarchyNodeResponse[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Armed in the SETUP, not only cleared in the teardown. React 19's StrictMode
  // mounts, unmounts and remounts every component in development; a ref set to
  // false on the first teardown and never re-armed latches false forever, and
  // the expand silently does nothing — in dev only, which is worse.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  // A refresh invalidates what was fetched — keeping it would show a subtree that
  // no longer exists after somebody's engagement ended. It COLLAPSES as well as
  // clearing: dropping the children while leaving the node open renders an open
  // chevron over nothing, with no spinner and no way to tell that from an empty
  // team.
  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    setChildren(null)
    setOpen(false)
  }, [refreshKey])

  const toggle = useCallback(async () => {
    if (open) { setOpen(false); return }
    setOpen(true)
    if (children !== null || !node.hasSubordinates) return
    setBusy(true); setError(null)
    try {
      const level = await hierarchyLevel(node.id)
      if (alive.current) setChildren(level)
    } catch (err) {
      if (alive.current) setError(explain(err))
    } finally {
      if (alive.current) setBusy(false)
    }
  }, [open, children, node.id, node.hasSubordinates])

  return (
    <li>
      <div
        className="flex items-center gap-2 py-1.5 rounded-md hover:bg-muted/40 min-w-0"
        style={{ paddingLeft: `${depth * 18}px` }}
      >
        {node.hasSubordinates ? (
          <button
            type="button"
            onClick={() => void toggle()}
            aria-expanded={open}
            aria-label={open ? `Collapse ${node.fullName}` : `Expand ${node.fullName}`}
            className="h-5 w-5 inline-flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" />
        )}

        <button
          type="button"
          onClick={() => onOpen(node.id)}
          className="min-w-0 flex-1 text-left flex flex-wrap items-center gap-2"
          data-node={node.fullName}
        >
          <span className="text-erp-sm font-medium truncate">{node.fullName}</span>
          <RoleBadge code={node.roleCode} />
          {node.statusCode !== 'ACTIVE' && <StatusChip code={node.statusCode} />}
          <span className="text-erp-xs text-muted-foreground truncate">
            {node.primaryUnitName ?? 'no home unit'}
          </span>
        </button>

        {node.subordinateCount > 0 && (
          <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground shrink-0"
                title={`${node.subordinateCount} direct reports`}>
            <Users className="h-3 w-3" /> {node.subordinateCount}
          </span>
        )}
      </div>

      {open && (
        <>
          {busy && (
            <div className={cn('text-erp-xs text-muted-foreground py-1')}
                 style={{ paddingLeft: `${(depth + 1) * 18 + 20}px` }}>
              Loading…
            </div>
          )}
          {error && (
            <div className="text-erp-xs text-red-700 dark:text-red-400 py-1"
                 style={{ paddingLeft: `${(depth + 1) * 18 + 20}px` }}>
              {error}
            </div>
          )}
          {children && children.length > 0 && (
            <ul className="border-l border-border/60 ml-2.5">
              {children.map((c) => (
                <TreeNode key={c.id} node={c} depth={depth + 1} onOpen={onOpen} refreshKey={refreshKey} />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  )
}
